import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";
import Gallery from "@/components/Gallery";
import { toast } from "sonner";
import { Zap, ChevronRight, MapPin, Calendar, Mail, ArrowUpRight, Cpu, Wrench, Users, Code2, Palette, Trophy, Rocket, Hammer, Target, Flag } from "lucide-react";
import { HeroBolts, SectionDecor, BoltDivider } from "@/components/Lightning";
import { useSettings } from "@/context/SettingsContext";

const LOGO = "https://customer-assets.emergentagent.com/job_79957589-3368-467d-ad2e-09a72cf10e61/artifacts/q42gzsj9_viperlogo.png";

const ROBOT_FALLBACK = [
  "https://images.unsplash.com/photo-1775826476148-1e35e8a74f09?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1775826476139-b5968f28ebdb?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1562758778-e5638b5b6607?crop=entropy&cs=srgb&fm=jpg&q=85",
];

const TEAM_FALLBACK = "https://images.unsplash.com/photo-1622476512221-5fe08e42f898?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const [team, setTeam] = useState([]);
  const [robots, setRobots] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [t, r, o, s] = await Promise.all([
          api.get("/team"),
          api.get("/robots"),
          api.get("/outreach"),
          api.get("/sponsors"),
        ]);
        if (cancelled) return;
        setTeam(t.data);
        setRobots(r.data);
        setOutreach(o.data);
        setSponsors(s.data);
      } catch (e) {
        // Fail silently for public load
      }
    };

    const refreshOutreach = async () => {
      try {
        const { data } = await api.get("/outreach");
        if (!cancelled) setOutreach(data);
      } catch (_e) {
        /* silent: best-effort polling */
      }
    };

    load();
    // Live updates: poll outreach every 30s, and refresh on tab focus
    const interval = setInterval(refreshOutreach, 30000);
    const onFocus = () => refreshOutreach();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return (
    <div className="bg-voltage-bg min-h-screen">
      <Header />
      <Hero />
      <Marquee />
      <About />
      <Pillars />
      <Team team={team} />
      <Gallery />
      <Robots robots={robots} />
      <SeasonTimeline />
      <Outreach outreach={outreach} />
      <Sponsors sponsors={sponsors} />
      <Contact />
      <Footer />
    </div>
  );
}

function Pillars() {
  const items = [
    {
      icon: Palette,
      title: "Design",
      body: "CAD modeling in Onshape, iterative prototyping, and mechanical engineering that turns a whiteboard sketch into a competition-ready machine.",
    },
    {
      icon: Hammer,
      title: "Build",
      body: "CNC, 3D printing, machining, wiring, and countless hours in the shop. Every screw, bracket, and belt gets our attention.",
    },
    {
      icon: Code2,
      title: "Code",
      body: "Java for the robot controller, autonomous routines, computer-vision targeting, and a control loop tuned to strike on command.",
    },
    {
      icon: Rocket,
      title: "Outreach",
      body: "Bringing robotics to younger students, running community events, and spreading the STEM spark to the next wave of engineers.",
    },
  ];

  return (
    <section id="pillars" data-testid="pillars-section" className="relative py-24 md:py-32 overflow-hidden">
      <SectionDecor variant="yellow" corners="br" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-6">
            <div className="overline mb-4">// What we do</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
              Four <span className="text-voltage-yellow">circuits</span>. One team.
            </h2>
          </div>
          <p className="lg:col-span-5 lg:col-start-8 text-voltage-muted text-base leading-relaxed">
            Robotics isn&apos;t just wrenches or just code. It&apos;s a full-stack sport that demands
            engineers, artists, programmers, and organizers all firing on the same current.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <article
              key={it.title}
              data-testid={`pillar-${it.title.toLowerCase()}`}
              className="group relative bg-voltage-surface border border-voltage-blue/30 p-6 min-h-[240px] flex flex-col hover:border-voltage-yellow transition-all duration-300"
            >
              <span className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-voltage-yellow/70" />
              <span className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-voltage-yellow/70" />
              <div className="flex items-center justify-between mb-6">
                <div className="p-3 bg-voltage-blue/10 border border-voltage-blue/40 group-hover:bg-voltage-yellow/10 group-hover:border-voltage-yellow/60 transition-colors">
                  <it.icon className="w-5 h-5 text-voltage-yellow" />
                </div>
                <span className="font-mono text-xs text-voltage-muted tracking-widest">0{i + 1}</span>
              </div>
              <h3 className="font-display font-black text-xl uppercase text-white mb-3">{it.title}</h3>
              <p className="text-sm text-voltage-muted leading-relaxed">{it.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SeasonTimeline() {
  const stages = [
    { icon: Flag, phase: "01", title: "Kickoff", when: "September", body: "New game announced. Rules dissected, strategy locked in." },
    { icon: Palette, phase: "02", title: "Design", when: "Oct — Nov", body: "CAD, prototypes, subsystem sketches. Every idea gets a chance." },
    { icon: Hammer, phase: "03", title: "Build", when: "Nov — Jan", body: "Machining, wiring, assembly. Robot takes physical shape." },
    { icon: Target, phase: "04", title: "Iterate", when: "Jan — Feb", body: "Drive practice, code tuning, ruthless testing." },
    { icon: Trophy, phase: "05", title: "Compete", when: "Feb — April", body: "Qualifiers, regionals, championship push." },
  ];

  return (
    <section id="season" data-testid="season-section" className="relative py-24 md:py-32 section-tint-yellow overflow-hidden">
      <SectionDecor variant="yellow" corners="tl" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="overline mb-4">// Season Journey</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mb-4">
          A season <span className="text-voltage-yellow">runs on 5 volts</span>
        </h2>
        <p className="text-voltage-muted max-w-2xl mb-12">
          From game reveal to championship day — how a build season charges up and detonates.
        </p>

        <div className="relative grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Horizontal connector line, desktop only */}
          <div className="hidden md:block absolute top-8 left-8 right-8 h-px bg-gradient-to-r from-voltage-yellow/40 via-voltage-blue/40 to-voltage-yellow/40" />

          {stages.map((s, i) => (
            <div
              key={s.phase}
              data-testid={`season-stage-${s.phase}`}
              className="relative"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="relative z-10 mb-4">
                <div className="w-16 h-16 bg-voltage-bg border-2 border-voltage-yellow flex items-center justify-center animate-pulse-glow">
                  <s.icon className="w-6 h-6 text-voltage-yellow" />
                </div>
              </div>
              <div className="font-mono text-[10px] text-voltage-muted tracking-widest uppercase mb-1">
                Phase {s.phase} · {s.when}
              </div>
              <div className="font-display font-black text-lg uppercase text-white mb-2 tracking-tight">
                {s.title}
              </div>
              <p className="text-sm text-voltage-muted leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EmptyStateCard({ testid, title, body, cta }) {
  return (
    <div
      data-testid={testid}
      className="relative border border-voltage-blue/40 bg-voltage-surface/40 p-10 md:p-14 overflow-hidden"
    >
      <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-voltage-yellow" />
      <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-voltage-yellow" />
      <div className="grid md:grid-cols-12 gap-8 items-center relative">
        <div className="md:col-span-8">
          <div className="overline mb-3">Under construction</div>
          <h3 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight text-white mb-3">
            {title}
          </h3>
          <p className="text-voltage-muted max-w-2xl leading-relaxed">{body}</p>
          {cta && (
            <a href={cta.href} className="btn-ghost mt-6 inline-flex">
              {cta.label} <ChevronRight className="w-4 h-4" />
            </a>
          )}
        </div>
        <div className="md:col-span-4 flex md:justify-end">
          <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
            <div className="absolute inset-0 border-2 border-voltage-blue/40 rotate-3" />
            <div className="absolute inset-3 border border-voltage-yellow/40 -rotate-3" />
            <Zap className="w-14 h-14 md:w-16 md:h-16 text-voltage-yellow animate-pulse-glow" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const { settings } = useSettings();
  const teamLabel = settings.team_number ? `Team #${settings.team_number}` : "Team [#TBD]";
  const teamNumDisplay = settings.team_number ? `#${settings.team_number}` : "[#TBD]";
  const parts = (settings.tagline || "Strike with Voltage. Engineer the Future.").split(/\.\s+|\.\s*$/).filter(Boolean);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-50" />
      <div className="absolute inset-0 bg-noise" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-voltage-blue/15 blur-[120px] rounded-full pointer-events-none" />
      <HeroBolts />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 grid lg:grid-cols-12 gap-10 items-center w-full">
        <div className="lg:col-span-7 animate-slide-up">
          <div className="overline mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-voltage-yellow" />
            FTC Robotics · {teamLabel}
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl uppercase leading-[0.95] tracking-tighter">
            {parts[0] && (
              <span className="block text-white">{parts[0]}.</span>
            )}
            {parts[1] && (
              <span className="block text-voltage-blue" style={{ WebkitTextStroke: "1px #1E3FD8", color: "transparent" }}>
                {parts[1]}.
              </span>
            )}
          </h1>
          <p className="mt-8 text-voltage-muted text-base sm:text-lg max-w-xl leading-relaxed">
            We are the <span className="text-white">{settings.team_name}</span> — a high-school FTC robotics team
            building competition robots, mentoring young engineers, and lighting up our community with STEM outreach.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#team" data-testid="hero-cta-team" className="btn-primary clip-bolt">
              <Zap className="w-4 h-4" /> Meet the Team
            </a>
            <a href="#robots" data-testid="hero-cta-robots" className="btn-ghost">
              See Our Robots <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-4 max-w-md">
            {[
              { v: "FTC", l: "League" },
              { v: teamNumDisplay, l: "Team Number" },
              { v: "1", l: "Goal: Win" },
            ].map((s, i) => (
              <div key={i} className="border border-voltage-blue/30 px-3 py-4">
                <div className="font-display font-black text-2xl text-voltage-yellow">{s.v}</div>
                <div className="text-xs text-voltage-muted uppercase tracking-widest mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative aspect-square max-w-md mx-auto">
            <div className="absolute inset-0 border-2 border-voltage-blue/40 -rotate-3" />
            <div className="absolute inset-4 border border-voltage-yellow/40 rotate-2" />
            <img
              src={settings.logo_url}
              alt={`${settings.team_name} Logo`}
              className="relative z-10 w-full h-full object-contain animate-pulse-glow"
              data-testid="hero-logo"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32 overflow-hidden section-tint-blue">
      <SectionDecor variant="blue" corners="tr" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="overline mb-4">// 01 — About</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mb-6 max-w-3xl">
          A team forged in <span className="text-voltage-yellow">lightning</span>.
        </h2>
        <BoltDivider />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-10">
          <div className="md:col-span-7 voltage-card p-8 md:p-10">
            <div className="overline mb-3">Mission</div>
            <p className="text-lg leading-relaxed text-white/90">
              We design, build, and program competition robots while inspiring the next wave of engineers,
              programmers, and creative problem-solvers. Every match is a chance to learn — every season,
              a chance to strike harder.
            </p>
          </div>

          {[
            { icon: Cpu, label: "Engineering", val: "CAD · CNC · 3D Print" },
            { icon: Wrench, label: "Build Season", val: "Sept — April" },
            { icon: Users, label: "Members", val: "Growing fast" },
          ].map(({ icon: Icon, label, val }, i) => (
            <div key={i} className="md:col-span-5 lg:col-span-5 voltage-card p-6 flex items-start gap-4">
              <div className="p-3 bg-voltage-blue/10 border border-voltage-blue/40">
                <Icon className="w-5 h-5 text-voltage-yellow" />
              </div>
              <div>
                <div className="overline">{label}</div>
                <div className="font-display font-bold text-xl text-white mt-1">{val}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Team({ team }) {
  const list = team.length > 0 ? team : [];
  return (
    <section id="team" data-testid="team-section" className="relative py-24 md:py-32 overflow-hidden section-tint-blue">
      <SectionDecor variant="blue" corners="both" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="overline mb-4">// 02 — Team</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
              Meet the <span className="text-voltage-yellow">Vipers</span>
            </h2>
          </div>
          <p className="text-voltage-muted max-w-md text-sm">
            Engineers, programmers, designers and strategists. Driven, curious, and ready to strike.
          </p>
        </div>

        {list.length === 0 ? (
          <EmptyStateCard
            testid="team-empty"
            title="Roster loading in"
            body="Team members will appear here as they join. Add them from the admin console — name, role, photo, and bio."
            cta={{ label: "Recruiting? Get in touch", href: "#contact" }}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {list.map((m) => (
              <article key={m.id} data-testid={`team-card-${m.id}`} className="voltage-card group">
                <div className="aspect-[4/5] overflow-hidden bg-black relative">
                  <img
                    src={m.photo_url || TEAM_FALLBACK}
                    alt={m.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    style={{ filter: "contrast(1.05)" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-voltage-blue/40 to-transparent group-hover:opacity-0 transition-opacity" />
                </div>
                <div className="p-5">
                  <div className="overline">{m.role}</div>
                  <h3 className="font-display font-bold text-xl text-white mt-2">{m.name}</h3>
                  {m.bio && <p className="text-sm text-voltage-muted mt-2 whitespace-pre-wrap">{m.bio}</p>}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Robots({ robots }) {
  return (
    <section id="robots" data-testid="robots-section" className="relative py-24 md:py-32 overflow-hidden">
      <SectionDecor variant="mixed" corners="br" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="overline mb-4">// 03 — Robots</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mb-12">
          The <span className="text-voltage-yellow">Hardware</span>
        </h2>

        {robots.length === 0 ? (
          <EmptyStateCard
            testid="robots-empty"
            title="Robot builds incoming"
            body="Once the season is underway, our competition robot lives here — full photos, subsystem specs, and season notes."
            cta={{ label: "Sponsor our next build", href: "#sponsors" }}
          />
        ) : (
          <div className="space-y-12">
            {robots.map((r, i) => (
              <article
                key={r.id}
                data-testid={`robot-card-${r.id}`}
                className={`grid lg:grid-cols-12 gap-8 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden border border-voltage-blue/40 bg-black">
                  <img
                    src={r.photo_url || ROBOT_FALLBACK[i % ROBOT_FALLBACK.length]}
                    alt={r.name}
                    className="w-full h-full object-cover"
                  />
                  {r.is_current && (
                    <span className="absolute top-4 left-4 bg-voltage-yellow text-black px-3 py-1 font-display font-bold text-xs tracking-widest uppercase">
                      Current Build
                    </span>
                  )}
                </div>
                <div className="lg:col-span-5">
                  <div className="font-mono text-voltage-yellow text-sm tracking-widest">SEASON {r.year}</div>
                  <h3 className="font-display font-black text-3xl lg:text-4xl uppercase mt-2 text-white">{r.name}</h3>
                  {r.description && <p className="text-voltage-muted mt-4 leading-relaxed">{r.description}</p>}
                  {r.specs?.length > 0 && (
                    <ul className="mt-6 space-y-2 font-mono text-sm">
                      {r.specs.map((s, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-white/80">
                          <span className="text-voltage-yellow mt-1">▸</span> {s}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Outreach({ outreach }) {
  const sorted = [...outreach].sort((a, b) => new Date(a.date) - new Date(b.date));
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = sorted.filter((e) => e.date >= today && !e.is_past);
  const past = sorted.filter((e) => e.date < today || e.is_past).reverse();

  return (
    <section id="outreach" data-testid="outreach-section" className="relative py-24 md:py-32 bg-voltage-surface/50">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="overline">// 04 — Outreach</div>
          <div data-testid="outreach-live-indicator" className="flex items-center gap-2 text-xs font-mono text-voltage-muted uppercase tracking-widest">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-voltage-yellow opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-voltage-yellow"></span>
            </span>
            Live · auto-updates
          </div>
        </div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mb-4">
          Outreach <span className="text-voltage-yellow">Schedule</span>
        </h2>
        <p className="text-voltage-muted max-w-2xl mb-6">
          We bring robotics to schools, libraries, and community events. Catch us in person — we love talking shop.
        </p>
        <BoltDivider />

        <div className="grid lg:grid-cols-2 gap-10 mt-10">
          <EventList title="Upcoming" testid="upcoming-events" events={upcoming} accent />
          <EventList title="Past Events" testid="past-events" events={past} />
        </div>
      </div>
    </section>
  );
}

function EventList({ title, events, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className={`overline mb-4 ${accent ? "text-voltage-yellow" : "text-white/60"}`}>{title}</div>
      {events.length === 0 ? (
        <div className="border border-dashed border-voltage-blue/30 p-8 text-voltage-muted text-sm">
          No {title.toLowerCase()} listed.
        </div>
      ) : (
        <ul className="divide-y divide-voltage-blue/20 border-y border-voltage-blue/20">
          {events.map((e) => (
            <li key={e.id} className="py-5 grid grid-cols-12 gap-4 items-start group hover:bg-voltage-blue/5 transition-colors px-2">
              <div className="col-span-3">
                <div className="font-mono text-voltage-yellow text-sm flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(e.date)}
                </div>
              </div>
              <div className="col-span-9">
                <div className="font-display font-bold text-lg text-white uppercase tracking-wide">{e.title}</div>
                {e.location && (
                  <div className="text-xs text-voltage-muted mt-1 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> {e.location}
                  </div>
                )}
                {e.description && <p className="text-sm text-voltage-muted mt-2">{e.description}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDate(iso) {
  try {
    // Parse YYYY-MM-DD as a LOCAL date so it doesn't shift by a day in
    // timezones west of UTC. new Date("2026-01-15") would otherwise be
    // parsed as UTC midnight and render as Jan 14 in the Americas.
    if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}/.test(iso)) {
      const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

// Local-timezone YYYY-MM-DD for comparing event dates against "today"
function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Sponsors section

function Sponsors({ sponsors }) {
  return (
    <section id="sponsors" data-testid="sponsors-section" className="relative py-24 md:py-32 overflow-hidden">
      <SectionDecor variant="yellow" corners="tl" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="overline mb-4">// 05 — Sponsors</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mb-12">
          Powered by our <span className="text-voltage-yellow">backers</span>
        </h2>

        {sponsors.length === 0 ? (
          <div data-testid="sponsors-empty" className="border border-dashed border-voltage-blue/40 p-10 text-center text-voltage-muted">
            We&apos;re building our sponsor wall. Want to be the first?{" "}
            <a href="#contact" className="text-voltage-yellow underline">Reach out</a>.
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {sponsors.map((s) => (
              <SponsorCard key={s.id} sponsor={s} />
            ))}
          </div>
        )}

        <div className="mt-16 border border-voltage-yellow/50 p-8 relative">
          <BoltDivider />
          <div className="mt-4 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="font-display font-black text-2xl uppercase">Become a sponsor</div>
              <p className="text-voltage-muted mt-2 max-w-xl">
                Help fund parts, travel, and outreach. Your logo lights up our pit and our season.
              </p>
            </div>
            <a href="#contact" data-testid="sponsor-cta" className="btn-primary clip-bolt">
              <Mail className="w-4 h-4" /> Sponsor Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SponsorCard({ sponsor }) {
  const isClickable = !!sponsor.link;
  const Component = isClickable ? "a" : "div";
  const props = isClickable ? { href: sponsor.link, target: "_blank", rel: "noreferrer" } : {};
  const accent = "#F5C518";

  return (
    <Component
      {...props}
      data-testid={`sponsor-${sponsor.id}`}
      className="group relative bg-voltage-bg border border-voltage-blue/40 aspect-square p-6 flex flex-col items-center justify-center text-center transition-all duration-300 hover:-translate-y-1 hover:border-voltage-yellow"
    >
      {/* Decorative corner notches */}
      <span className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-voltage-yellow/70" />
      <span className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-voltage-yellow/70" />

      {sponsor.logo_url ? (
        <img
          src={sponsor.logo_url}
          alt={sponsor.name}
          className="max-h-full max-w-full object-contain grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all"
        />
      ) : (
        <span className="font-display font-black text-base uppercase tracking-wide text-white">
          {sponsor.name}
        </span>
      )}

      {isClickable && (
        <span
          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: accent }}
        >
          <ArrowUpRight className="w-4 h-4" />
        </span>
      )}
    </Component>
  );
}

function Contact() {
  const { settings } = useSettings();
  const teamNumLabel = settings.team_number ? `#${settings.team_number}` : "TBD";
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 section-tint-blue overflow-hidden">
      <SectionDecor variant="mixed" corners="bl" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-start relative">
        <div className="lg:col-span-5">
          <div className="overline mb-4">// 06 — Contact</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
            Get in <span className="text-voltage-yellow">touch</span>.
          </h2>
          <p className="text-voltage-muted mt-6 max-w-md">
            Coach inquiries, sponsorship deals, outreach invitations, or you just want to say hi —
            we read every message.
          </p>
          <div className="mt-8 max-w-sm">
            <BoltDivider />
          </div>
          <div className="mt-6 space-y-3 text-sm font-mono">
            <div className="flex items-center gap-3 text-white/80">
              <ArrowUpRight className="w-4 h-4 text-voltage-yellow" /> voltagevipersftcsd@gmail.com
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <ArrowUpRight className="w-4 h-4 text-voltage-yellow" /> Team Number: {teamNumLabel}
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 voltage-card p-8 md:p-10 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Name" required value={form.name} onChange={update("name")} testid="contact-name" />
            <Field label="Email" type="email" required value={form.email} onChange={update("email")} testid="contact-email" />
          </div>
          <Field label="Subject" value={form.subject} onChange={update("subject")} testid="contact-subject" />
          <div>
            <label className="overline mb-2 block">Message *</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message"
              className="w-full bg-transparent border-b-2 border-voltage-blue/60 focus:border-voltage-yellow outline-none py-2 text-white placeholder:text-voltage-muted resize-none transition-colors"
              placeholder="Tell us what's up..."
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="btn-primary clip-bolt disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Zap className="w-4 h-4" /> {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, type = "text", required, value, onChange, testid }) {
  return (
    <div>
      <label className="overline mb-2 block">{label}{required && " *"}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border-b-2 border-voltage-blue/60 focus:border-voltage-yellow outline-none py-2 text-white placeholder:text-voltage-muted transition-colors"
      />
    </div>
  );
}
